
import React, { useEffect } from 'react';
import { Card, CardContent } from "@/components/ui/card";
import { useToast } from "@/components/ui/use-toast";
import { Form } from "@/components/ui/form";
import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import { UserTypeSelector } from './UserTypeSelector';
import { ExistingUserSelector } from './ExistingUserSelector';
import { NewUserForm } from './NewUserForm';
import { useUsersQuery } from './useUsersQuery';
import { userAssignmentSchema, UserAssignmentFormValues } from './types';

interface UserAssignmentFormProps {
  onSubmit: (data: any, isValid: boolean) => void;
  initialData?: any;
}

export const UserAssignmentForm = ({ onSubmit, initialData }: UserAssignmentFormProps) => {
  const { toast } = useToast();
  
  // Create form with validation
  const form = useForm<UserAssignmentFormValues>({
    resolver: zodResolver(userAssignmentSchema),
    defaultValues: initialData ? {
      assignmentType: initialData.assignmentType || 'existing',
      id: initialData.id || '',
      email: initialData.email || '',
      firstName: initialData.firstName || '',
      lastName: initialData.lastName || '',
      password: initialData.password || generateStrongPassword()
    } : {
      assignmentType: 'existing',
      id: '',
      email: '',
      firstName: '',
      lastName: '',
      password: generateStrongPassword()
    },
    mode: "onChange"
  });
  
  // Fetch users with strategy_provider role
  const { data: strategyProviderUsers, isLoading: isLoadingUsers, error: usersError } = useUsersQuery();
  
  // Watch assignment type to conditionally render
  const assignmentType = form.watch('assignmentType');
  const formState = form.formState;
  
  // Use a ref to track if the form has been mounted
  const isMounted = React.useRef(false);
  
  useEffect(() => {
    if (!isMounted.current) {
      isMounted.current = true;
      return;
    }

    // Only notify parent about form validity after initial render
    const formValues = form.getValues();
    onSubmit({ user: formValues }, formState.isValid);
    
  }, [formState.isValid, formState.isDirty, form, onSubmit]);
  
  const handleFormSubmit = (data: UserAssignmentFormValues) => {
    onSubmit({ user: data }, true);
  };

  // Helper function to check if id field has errors when assignmentType is 'existing'
  const hasIdError = () => {
    if (assignmentType !== 'existing') return false;
    
    // Get all form errors
    const errors = form.formState.errors;
    
    // Check if there are errors for the id field when assignment type is 'existing'
    return assignmentType === 'existing' && 
           errors.assignmentType?.type !== 'invalid_literal' && 
           'id' in errors;
  };

  // Handle query error gracefully
  if (usersError) {
    toast({
      title: "Error loading users",
      description: "Could not load strategy provider users",
      variant: "destructive",
    });
  }

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(handleFormSubmit)}>
        <Card>
          <CardContent className="pt-6">
            <div className="space-y-4">
              <UserTypeSelector form={form} />

              {assignmentType === 'existing' ? (
                <ExistingUserSelector 
                  form={form}
                  strategyProviderUsers={strategyProviderUsers || []}
                  isLoadingUsers={isLoadingUsers}
                  hasErrors={hasIdError()}
                />
              ) : (
                <NewUserForm form={form} />
              )}
            </div>
          </CardContent>
        </Card>
      </form>
    </Form>
  );
};

function generateStrongPassword(length = 12) {
  const charset = "abcdefghijklmnopqrstuvwxyzABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789!@#$%^&*()_+";
  let password = "";
  for (let i = 0; i < length; i++) {
    const randomIndex = Math.floor(Math.random() * charset.length);
    password += charset[randomIndex];
  }
  return password;
}
